import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { showSuccessToast } from '../utils/dialogs';
import { USERNAME_PATTERN } from '../constant/teacherLogin';

const INPUT_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]';

function PasswordInput({ id, value, onChange, show, autoComplete }) {
  return (
    <input
      id={id}
      type={show ? 'text' : 'password'}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT_CLASS}
    />
  );
}

// Username and password for the signed-in student or teacher, saved separately from anything else on
// their profile since both need the current password. `save` gets { currentPassword, username,
// newPassword } and `onSaved` the new username. `mustChange` is for an account still on the login
// staff gave it: a new password is then required.
function LoginDetailsForm({ username: savedUsername, save, onSaved, mustChange = false }) {
  const [username, setUsername] = useState(savedUsername ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const login = username.trim();
  const changed = mustChange || login !== (savedUsername ?? '') || newPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!USERNAME_PATTERN.test(login)) {
      setError('Usernames are 3-50 letters, numbers, dots, dashes or underscores.');
      return;
    }
    if (mustChange && !newPassword) {
      setError('Choose a new password to continue.');
      return;
    }
    if (newPassword && newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The new passwords don't match.");
      return;
    }
    if (!currentPassword) {
      setError('Enter your current password to save these changes.');
      return;
    }
    if (newPassword && newPassword === currentPassword) {
      setError('Your new password must be different from your current one.');
      return;
    }

    setSaving(true);
    try {
      await save({ currentPassword, username: login, newPassword });
      setUsername(login);
      setNewPassword('');
      setConfirmPassword('');
      setCurrentPassword('');
      showSuccessToast(newPassword ? 'Login details updated' : 'Username updated');
      onSaved(login);
    } catch (err) {
      setError(err.message || 'Failed to update your login details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-lg text-[#0b4a71]">Login Details</h2>
        <button
          type="button"
          onClick={() => setShowPasswords((s) => !s)}
          className="flex items-center gap-1 text-lg font-medium text-gray-500 hover:text-[#0b4a71]"
        >
          {showPasswords ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          {showPasswords ? 'Hide passwords' : 'Show passwords'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

      <div className="space-y-1">
        <label htmlFor="username" className="text-lg font-medium text-gray-700">Username</label>
        <input
          id="username"
          type="text"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label htmlFor="new-password" className="text-lg font-medium text-gray-700">New Password</label>
          <PasswordInput
            id="new-password"
            value={newPassword}
            onChange={setNewPassword}
            show={showPasswords}
            autoComplete="new-password"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="confirm-password" className="text-lg font-medium text-gray-700">Confirm New Password</label>
          <PasswordInput
            id="confirm-password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showPasswords}
            autoComplete="new-password"
          />
        </div>
      </div>
      <p className="text-lg italic text-gray-500 -mt-1">
        {mustChange
          ? 'At least 8 characters, and different from the password you were given.'
          : 'Leave the new password blank to keep your current one. Changing it signs you out on your other devices.'}
      </p>

      {changed && (
        <div className="space-y-1">
          <label htmlFor="current-password" className="text-lg font-medium text-gray-700">Current Password</label>
          <PasswordInput
            id="current-password"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showPasswords}
            autoComplete="current-password"
          />
        </div>
      )}

      <button
        type="submit"
        disabled={saving || !changed}
        className="w-full rounded-md bg-[#0b4a71] py-2.5 text-lg font-medium text-white hover:bg-[#093c5c] disabled:opacity-60"
      >
        {saving ? 'Saving…' : mustChange ? 'Save and Continue' : 'Update Login Details'}
      </button>
    </form>
  );
}

// Shown above the form while the account still has to choose its own login.
export function MustChangeLoginNotice() {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-lg text-amber-800">
      <p className="font-semibold">Welcome! Set up your own login first.</p>
      <p>
        You're signed in with the username and password you were given. Choose a new password (and a new
        username if you like) before you continue.
      </p>
    </div>
  );
}

export default LoginDetailsForm;
